<template>
  <BaseDialog
    :title="$t('survey.editPerspectives')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="emit('close-dialog')"
  >
    <div class="flex flex-col">
      <h3
        class="text-title-small mb-2"
        :class="[
          !errors.get('perspectives') && 'text-on-surface-variant',
          errors.get('perspectives') && 'text-error',
        ]"
      >
        {{ $t('startSurvey.selectPerspectives') }}
      </h3>
      <div class="flex flex-col gap-3">
        <PerspectiveGatheringItem
          v-for="perspective in availablePerspectives"
          :key="perspective.key"
          :perspective="perspective"
          :state="ensurePerspectiveState(perspective.key)"
          :required="requiredPerspectiveKeys.has(perspective.key)"
          :locked="perspectiveLocked(perspective.key)"
          :open-link-locked="openLinkLocked(perspective.key)"
          :locked-member-ids="lockedMemberIds(perspective.key)"
          :error="errors.get(perspective.key)"
          :alreadySelectedMemberIds="selectedMemberIds"
          member-capability="canGiveFeedback"
        />
      </div>
      <p v-if="errors.get('perspectives')" class="text-error text-body-medium my-2 px-4">
        {{ errors.get('perspectives') }}
      </p>
      <template v-if="showSelf">
        <hr class="border-outline-variant -mx-6 my-4" />
        <BaseSwitchListItem
          v-model="form.includeSelf"
          v-tooltip="
            selfLocked
              ? $t('survey.perspectiveAnsweredTooltip')
              : selfRequired
                ? $t('startSurvey.perspectiveRequiredTooltip')
                : undefined
          "
          :title="$t('startSurvey.addSelfFeedback')"
          :supporting-text="$t('startSurvey.addSelfFeedbackDescription')"
          icon="account_circle"
          :disabled="selfRequired || selfLocked"
          class="rounded-large bg-surface-container-low"
        />
      </template>
    </div>
    <template #confirm>
      <BaseTextButton color="filled" size="M" :disabled="isSaving" @click="save">
        {{ $t('common.save') }}
      </BaseTextButton>
    </template>
    <template #dismiss>
      <BaseTextButton color="text" size="M" :disabled="isSaving" @click="emit('close-dialog')">
        {{ $t('common.cancel') }}
      </BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import { toRef } from 'vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseSwitchListItem from '@/core/components/base/switch-list-item/BaseSwitchListItem.vue'
  import PerspectiveGatheringItem from '@/core/components/perspective-gathering/PerspectiveGatheringItem.vue'
  import type { Survey } from '../models/survey'
  import { useEditSurveyPerspectives } from '../composables/use-edit-survey-perspectives'

  interface Props {
    isOpen: boolean
    survey: Survey
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'close-dialog'): void
    (e: 'survey-updated'): void
  }>()

  const {
    form,
    errors,
    availablePerspectives,
    showSelf,
    selfRequired,
    selfLocked,
    requiredPerspectiveKeys,
    ensurePerspectiveState,
    selectedMemberIds,
    lockedMemberIds,
    openLinkLocked,
    perspectiveLocked,
    save,
    isSaving,
  } = useEditSurveyPerspectives(toRef(props, 'survey'), toRef(props, 'isOpen'), () => {
    emit('survey-updated')
    emit('close-dialog')
  })
</script>
